/**
 * ONE DEFINITION of Jr Staff, everywhere in the Hub.
 *
 * Bobbie's standing ruling: Jr Staff is role='ensemble' users PLUS the
 * dancers a Director designated Jr Staff (see jrStaffDesignations.ts) whose
 * family has since created a working dancer login from Account. Both
 * designations get the same ACCESS eventually; the rules-parity build that
 * makes that true is separate work and is not what this file does.
 *
 * A designated dancer without a linked login carries no identifier at all
 * here, on purpose. There is nothing yet that could hold a class assignment
 * or an attendance mark for them, and inventing a placeholder id would be
 * exactly the fake state the bridge exists to stop. A caller that needs to
 * say so out loud should say something honest ("assignable once their login
 * is linked"), never silently pretend the gap is not there.
 *
 * A dancer login's uid is a Firestore identity, never a Firebase Auth uid
 * (see dancerSession.ts: dancer sessions are an opaque server-owned cookie,
 * not an ID token). Recording that uid here does not grant it any new
 * capability - whether it can actually act as staff once assigned is the
 * separate rules-parity build. See HELPER-FIREBASE-RULES.md / DECISIONS.md
 * for that lane.
 */

import type { UserDoc } from './users.ts';
import { jrStaffEmailAccounts } from './jrStaffEmailAccounts.ts';
import {
  canonicalJrStaffAliasUid,
  isOwnJrStaffSignIn,
  jrStaffAliasCandidateWins,
  jrStaffAliasSets,
} from './jrStaffAliases.ts';
import {
  UNNAMED_PERSON_LABEL,
  teacherDisplayName,
  teacherNameForFamilyCopy,
} from './teacherTitle.ts';
import type { JrStaffAssignmentStatus } from './jrStaffDesignations.ts';

/** Firestore's physical offering.assistantUids ceiling. Linked sign-ins can use two seats. */
export const MAX_ASSISTANT_ACCOUNT_ASSIGNMENTS = 12;

/** Refuse oversized legacy/editor state instead of silently slicing people off a save. */
export function assistantAccountLimitError(uids: readonly string[]): string | null {
  return uids.length > MAX_ASSISTANT_ACCOUNT_ASSIGNMENTS
    ? `This class has ${uids.length} assistant account assignments, above the limit of ${MAX_ASSISTANT_ACCOUNT_ASSIGNMENTS}. Remove an assistant before saving; nobody was dropped.`
    : null;
}

// Only a TYPE import from ./users on purpose: that module pulls in the real
// Firebase SDK at load time, the same reason lib/familyGroups.ts and
// lib/staffAttendanceMath.ts stay import-free. `import type` is erased by
// node's type stripping, so this file (and any test that loads it directly)
// never touches Firebase. isPromotedDancerLogin and isLiveDancerLogin below
// deliberately mirror isDancerLoginUserDoc in ./users and the bridge's
// isPromotedDancerLogin / isEnabledDancerLoginShape
// (netlify/functions/lib/jrStaffDancerBridge.mjs) rather than importing
// either as a value; keep them in lockstep if any of them changes.

/** The fields these predicates read. Narrow on purpose, so a caller holding
 * a partial account row (a picker option, a test fixture) still type-checks. */
type DancerLoginShapeFields = Pick<UserDoc, 'role' | 'status' | 'studentId'>
  & Partial<Pick<UserDoc, 'jrStaffPromoted'>>;

/**
 * A PROMOTED dancer login: the same human, wearing the shape Jr Staff
 * parity gives her.
 *
 * ROLE AND STATUS STOP IDENTIFYING A DANCER LOGIN THE MOMENT SHE IS
 * PROMOTED, and this file used to assume they never would. Promotion
 * rewrites her users doc to role 'ensemble', status 'active' on purpose -
 * that is the parity, being the same thing rather than a parallel branch
 * (netlify/functions/lib/jrStaffDancerBridge.mjs). Anything below spelled
 * `status === 'dancer'` therefore answered "no" for her, and the fallout
 * landed on two screens at once: the class editor filed her under
 * "waiting on their family to make a dancer login" while listing her in
 * the picker directly above that sentence, and the one-human-one-entry
 * dedupe stopped firing, so a promoted login plus an identity-linked adult
 * ensemble account showed the same person twice in the picker and twice in
 * the Staff directory - and StaffDancerView, seeing two, hid her Jr Staff
 * panel from her own page.
 *
 * KEPT IN LOCKSTEP BY HAND with isPromotedDancerLogin in
 * netlify/functions/lib/jrStaffDancerBridge.mjs, deliberately the same
 * marker-plus-role test and deliberately NOT an authMode test: legacy
 * Firebase-backed dancer records carry no authMode until they migrate, and
 * the bridge's own comment says folding authMode in here broke the whole
 * legacy lane on first run. This file cannot import either module without
 * pulling Firebase in at load time (see the note above), so the lockstep is
 * a promise, not an import. Change one, change all three (the third is
 * isLiveDancerLoginJrStaffUser in netlify/functions/lib/jrStaffLinks.mjs).
 */
function isPromotedDancerLogin(user: DancerLoginShapeFields): boolean {
  if (typeof user.studentId !== 'string' || user.studentId.trim().length === 0) return false;
  return user.jrStaffPromoted === true && user.role === 'ensemble';
}

/**
 * True for ANY dancer login account, live or switched off, promoted or not:
 * the account shape createDancerLogin in netlify/functions/dancerLogin.mjs
 * writes (role 'family', studentId set, no staff role) in either status,
 * PLUS the promoted shape the parity build writes over it. Broader than
 * isLiveDancerLogin on purpose - this is how designatedLoginGaps below
 * tells "the family never made one" apart from "they made one and it is
 * switched off right now", instead of collapsing both into one silent gap.
 */
function isDancerLoginShapedUser(user: DancerLoginShapeFields): boolean {
  if (typeof user.studentId !== 'string' || user.studentId.trim().length === 0) return false;
  if (isPromotedDancerLogin(user)) return true;
  return !['director', 'manager', 'company', 'ensemble', 'foh'].includes(user.role);
}

/**
 * Every identity-linked account shape whose stored uid can hold a Jr Staff
 * class assignment for this student. This is a read-only diagnostic
 * projection for account cards: active, inactive, and retired records stay
 * present so a Director can see a stale class seat without granting any
 * authority or deciding who is Jr Staff.
 *
 * The roster's dancer-login predicate and ensemble shape are the only
 * accepted account shapes. A staff account from another role that happens to
 * share a studentId cannot enter this projection.
 */
export function jrStaffAssignmentAccountsForStudent(
  users: readonly UserDoc[],
  studentId: string | null | undefined
): UserDoc[] {
  if (typeof studentId !== 'string' || studentId.length === 0) return [];
  return users.filter((user) => user.studentId === studentId
    && (user.role === 'ensemble' || isDancerLoginShapedUser(user)));
}

/**
 * True for a dancer login that is currently usable. The enabled half of
 * each shape, matching isEnabledDancerLoginShape in the bridge exactly: an
 * unpromoted login is live at status 'dancer', a promoted one at status
 * 'active', and 'inactive' is the switched-off half of both - the same flip
 * manageLogin uses for enable/disable, so a disabled login is never picked
 * up here in either shape.
 */
function isLiveDancerLogin(user: DancerLoginShapeFields): boolean {
  if (!isDancerLoginShapedUser(user)) return false;
  return isPromotedDancerLogin(user) ? user.status === 'active' : user.status === 'dancer';
}

/**
 * Guardian uids per dancer, exactly as students/{id}.familyUids reads. The
 * SECOND door onto "these two accounts are one human", and the reason it is
 * needed: an older adult Jr Staff account is linked to its dancer through
 * users/{uid}.studentId (the identity link) OR through her familyUids alone,
 * because roughly two dozen accounts predate the identity-link lane and
 * carry only the family one. A caller that cannot load students may leave
 * this out; it only ever collapses MORE duplicates, never fewer.
 */
export type FamilyUidsByStudent = ReadonlyMap<string, readonly string[]>;

/**
 * DOOR TWO onto "these two accounts might be one human", stated ONCE: the
 * active ensemble accounts that sit in a dancer's students/{id}.familyUids
 * and carry NO identity link of their own (users/{uid}.studentId unset).
 *
 * Fixed 2026-09-02: this door used to collapse the same way the identity
 * link does, on the premise that an unlinked active ensemble account in a
 * Jr Staff dancer's familyUids "is her own older account and not her
 * parent". familyUids lands an account there for a SIBLING exactly as
 * readily (acceptInvite.mjs arrayUnions the accepting uid onto every
 * invited dancer; tempPassword.mjs's studentIds loop runs for every role).
 * Anna, 17, Jr Staff with her own ensemble account and no dancer login of
 * her own, sat in her sister Quinn's familyUids: the old code dropped Anna
 * from the picker outright and put her uid in Quinn's legacyUids, so one
 * tap on Quinn's page ran the removal loop and stripped Anna off her own
 * class. Two different humans wear this shape identically, so the door can
 * only ever FLAG a possible match. It never collapses a roster, never feeds
 * a removal loop, and never decides whether somebody is Jr Staff.
 *
 * It still names the roughly two dozen accounts that predate the identity
 * link (Bobbie: "we also have some doubles happening now.... same person"),
 * as a flag a Director acts on in Manage Users, not as a silent merge.
 *
 * One rule, one spelling: resolveJrStaffIdentities projects it onto every
 * identity, and resolveJrStaffDancerPanel asks it about one dancer.
 */
function familyOnlyEnsembleUids(
  users: readonly UserDoc[],
  familyUids: readonly string[] | undefined
): string[] {
  const wanted = new Set(
    (familyUids ?? [])
      .map((uid) => (typeof uid === 'string' ? uid.trim() : ''))
      .filter((uid) => uid.length > 0)
  );
  if (wanted.size === 0) return [];
  return users
    .filter(
      (u) => u.status === 'active' && u.role === 'ensemble' && !u.studentId && wanted.has(u.uid)
    )
    .map((u) => u.uid)
    .sort((a, b) => a.localeCompare(b));
}

/** One order for every duplicate list, so two flags never disagree. */
function byStudentThenEnsembleUid(
  a: JrStaffDuplicateIdentity,
  b: JrStaffDuplicateIdentity
): number {
  return a.studentId.localeCompare(b.studentId) || a.ensembleUid.localeCompare(b.ensembleUid);
}

/**
 * Resolve the Jr Staff roster from already-loaded data: every active
 * role='ensemble' account, plus a designated dancer whose live login is
 * present in the same `users` list. Alphabetical by display name, so every
 * caller (the class builder's Jr assistant picker, the attendance Jr roster)
 * shows the same order without re-sorting.
 *
 * DERIVED, NOT RE-DERIVED. This used to carry its own multi-pass copy of the
 * membership rule, the account precedence and the one-human-one-entry
 * dedupe, which is exactly the shape that let six surfaces drift apart in
 * one evening. It is now a projection of resolveJrStaffIdentities: the
 * people who have an account at all, in that resolver's order. A designated
 * dancer with no account carries no uid a picker could hold, and
 * designatedLoginGaps below is how a surface says so out loud.
 *
 * `familyUidsByStudent` is optional and only ever adds a FLAG (door two
 * above); the roster it returns is identical with or without it.
 */
export function resolveJrStaffUsers(
  users: readonly UserDoc[],
  designations: readonly JrStaffAssignmentStatus[],
  familyUidsByStudent?: FamilyUidsByStudent
): UserDoc[] {
  return resolveJrStaffIdentities({ users, designations, familyUidsByStudent })
    .map((identity) => identity.account)
    .filter((account): account is UserDoc => account !== null);
}

/**
 * The dancer's own live login for a student, or null. Exported for surfaces
 * that need to name her SIGN-IN specifically - her own page says out loud
 * that she has one and that it stays a dancer view.
 *
 * It is no longer the account classes attach to. Bobbie, 2026-09-02: *"Keep
 * both, email account wins: lists show the email account; the dancer sign-in
 * stays a dancer view."* Ask resolveJrStaffIdentities for that, never this.
 */
export function liveDancerLoginForStudent(
  studentId: string,
  users: readonly UserDoc[]
): UserDoc | null {
  if (!studentId) return null;
  return users.find((u) => u.studentId === studentId && isLiveDancerLogin(u)) ?? null;
}

/** One displaced adult account, so a surface can flag it honestly. */
export interface JrStaffDuplicateIdentity {
  /** The ensemble ("adult") account the roster dropped. */
  ensembleUid: string;
  /** The dancer login that replaced it. */
  dancerLoginUid: string;
  /** The dancer both accounts are server-linked to. */
  studentId: string;
}

/**
 * Every ensemble account resolveJrStaffUsers displaced in favor of the same
 * person's dancer login. Flag-only by Bobbie's explicit instruction
 * (2026-08-19: "Just flag it, don't touch data") - nothing here or in the
 * resolver mutates or merges the accounts, this only lets a picker say
 * "this person also has an older staff account" instead of silently
 * shrinking by one.
 *
 * A PROMOTED dancer login counts as the dancer login here, through the
 * shared isLiveDancerLogin. She is herself an active ensemble account, so
 * the `login.uid !== u.uid` test below is what keeps her from being reported
 * as her own duplicate; only a SECOND, separate ensemble account linked to
 * the same dancer is. Before that, promotion made this function go quiet
 * exactly when it had something to say, and the roster went quiet with it -
 * so the same human rendered twice with nothing on screen naming why.
 */
export function duplicateJrStaffAdultAccounts(
  users: readonly UserDoc[],
  designations: readonly JrStaffAssignmentStatus[],
  familyUidsByStudent?: FamilyUidsByStudent
): JrStaffDuplicateIdentity[] {
  // DERIVED from resolveJrStaffIdentities rather than re-walking `users`, so
  // the list of accounts the roster dropped and the list it names here can
  // never disagree. They used to be two separate passes kept in step by hand.
  //
  // No designation gate (review finding F2): the ensemble link itself is
  // what makes the pair a duplicate identity, exactly matching the resolver's
  // displacement rule.
  //
  // Only DOOR ONE (the identity link, users/{uid}.studentId) ever lands here.
  // Door two never removes anyone from the roster, so it can never appear in
  // this list - see possibleJrStaffDuplicates below for that one. And only a
  // pair whose surviving account is the dancer's OWN live sign-in is reported,
  // which is what the field names promise; two adult ensemble accounts on one
  // dancer are a different fact, carried on the identity's
  // duplicateAccountUids and flagged by the dancer's own page.
  const byUid = new Map(users.map((u) => [u.uid, u]));
  const out: JrStaffDuplicateIdentity[] = [];
  for (const identity of resolveJrStaffIdentities({ users, designations, familyUidsByStudent })) {
    const survivor = identity.account;
    if (!identity.studentId || !survivor) continue;
    // THE PAIR, WHICHEVER WAY ROUND IT SITS. Since 2026-09-02 the email
    // account is the survivor and her sign-in is the displaced one, so this
    // reports {ensembleUid: the email account, dancerLoginUid: her sign-in}
    // by finding the sign-in among the displaced. Before that ruling the two
    // roles were swapped; the field names describe the two ACCOUNTS, not who
    // won, so they did not have to change.
    for (const uid of identity.duplicateAccountUids) {
      const other = byUid.get(uid);
      if (!other) continue;
      const emailAccount = isLiveDancerLogin(other) ? survivor : other;
      const dancerLogin = isLiveDancerLogin(other) ? other : survivor;
      // Only a real pair of one email account and one sign-in is reported
      // here; two email accounts on one dancer are a different fact, carried
      // on duplicateAccountUids and flagged by the dancer's own page.
      if (!isLiveDancerLogin(dancerLogin)) continue;
      if (emailAccount.status !== 'active' || emailAccount.role !== 'ensemble') continue;
      if (isLiveDancerLogin(emailAccount)) continue;
      out.push({
        ensembleUid: emailAccount.uid,
        dancerLoginUid: dancerLogin.uid,
        studentId: identity.studentId,
      });
    }
  }
  return out.sort(byStudentThenEnsembleUid);
}

/**
 * Every active ensemble account that MIGHT be the same person as a dancer
 * whose live login is on the roster, matched only through students/{id}.
 * familyUids with no identity link of its own (users/{uid}.studentId unset).
 * Never collapsed into the roster and never touches data - familyUids alone
 * cannot tell "her own older account" from "her sibling's account" (both
 * accounts land there through acceptInvite.mjs and tempPassword.mjs for
 * every invited or created family member), so a caller can only say
 * "possible same person, link them in Manage Users" and let a Director
 * decide with the identity link, never assume it for them.
 *
 * The same one walk as the roster and the real duplicates: this reads
 * `possibleDuplicateUids` off the shared identity. A live sign-in of her own
 * is required only because this shape has to NAME one; the dancer's own page
 * asks door two directly and so can flag a match for a dancer who has no
 * sign-in at all.
 */
export function possibleJrStaffDuplicates(
  users: readonly UserDoc[],
  designations: readonly JrStaffAssignmentStatus[],
  familyUidsByStudent?: FamilyUidsByStudent
): JrStaffDuplicateIdentity[] {
  const byUid = new Map(users.map((u) => [u.uid, u]));
  const out: JrStaffDuplicateIdentity[] = [];
  for (const identity of resolveJrStaffIdentities({ users, designations, familyUidsByStudent })) {
    if (!identity.studentId) continue;
    // Her own sign-in, wherever it sits. It is the survivor when she has no
    // email account and one of the displaced when she does (Bobbie,
    // 2026-09-02), and this flag has always been named against it.
    const login = [identity.accountUid, ...identity.duplicateAccountUids]
      .map((uid) => (uid ? byUid.get(uid) : undefined))
      .find((u) => !!u && isLiveDancerLogin(u));
    if (!login) continue;
    for (const ensembleUid of identity.possibleDuplicateUids) {
      out.push({ ensembleUid, dancerLoginUid: login.uid, studentId: identity.studentId });
    }
  }
  return out.sort(byStudentThenEnsembleUid);
}

/** Everything one dancer's own page needs to decide about her Jr Staff panel. */
export interface JrStaffDancerPanel {
  /**
   * IS SHE JR STAFF. The Director's ledger designation
   * (studentJrStaffDesignations, mirrored onto students/{id}.jrStaffDesignated
   * by netlify/functions/setStudentJrStaff.mjs), OR an active ensemble account
   * server-linked to her, which is Jr Staff by that role itself.
   */
  isJrStaff: boolean;
  /** The account a class assignment attaches to, or null when there is none. */
  account: UserDoc | null;
  /** Her own live dancer sign-in, when one exists. */
  dancerLogin: UserDoc | null;
  /**
   * Every active ensemble account IDENTITY-linked to her
   * (users/{uid}.studentId === this dancer's id, proven by the server).
   * This is what decides `account`, `legacyUids` and `isJrStaff` - the only
   * door that ever collapses onto this dancer.
   */
  ensembleAccounts: UserDoc[];
  /**
   * Every OTHER account identity-linked to this same person: the uids
   * `account` displaced. Since 2026-09-02 that is usually her own dancer
   * sign-in, because her email account wins; before that ruling it was the
   * older adult account. A class already assigned under any of them has to
   * keep reading as assigned, or the count under-reports and toggling it on
   * puts one human into assistantUids twice. Identity-linked only - never
   * from `possibleEnsembleUids`, so toggling a class off can never remove a
   * SIBLING from it.
   */
  legacyUids: string[];
  /**
   * Active ensemble accounts that MIGHT be this same dancer, matched only
   * through students/{id}.familyUids with no identity link of their own.
   * Flag-only, on purpose: that door is walked by a sibling's invite
   * acceptance and temp-password grant exactly as readily as by the
   * account's own family (acceptInvite.mjs, tempPassword.mjs), so it can
   * never decide `account`, feed `legacyUids`, or count toward `isJrStaff` -
   * a surface can only say "possible same person, link them in Manage
   * Users" and let a Director confirm it with the identity link.
   */
  possibleEnsembleUids: string[];
  /**
   * Why nothing is assignable, when she IS Jr Staff and `account` is null.
   * Never collapsed into one silent gap: a sign-in that was switched off is
   * a different fact from one nobody ever made, and two rival adult accounts
   * is a third.
   */
  blocked: 'awaitingLogin' | 'loginDisabled' | 'ambiguousAccounts' | null;
}

/**
 * THE JR STAFF PANEL ON ONE DANCER'S OWN PAGE.
 *
 * THE GATE ASKS THE DESIGNATION, NOT THE ACCOUNT SHAPE. StaffDancerView used
 * to gate its whole Jr Staff panel on "does an active ensemble account link
 * to this dancer", with a comment whose REASONING was right - "having a login
 * is not being Jr Staff", a plain dancer with a sign-in must never grow a Jr
 * Staff panel - and whose implementation used the wrong question. A dancer
 * sign-in is role 'family', status 'dancer', so it is never in that ensemble
 * list; for a dancer the Director designated whose ONLY sign-in is her own
 * (Emily Donnici, Bobbie's live blocker on 2026-09-01, whose Manage Users
 * card reads "Jr Staff assignment: Assigned" and "Their own Hub sign-in:
 * their own dancer sign-in"), the list was empty, the panel never rendered,
 * and she could not be attached to the class she teaches next week.
 *
 * ONE WALK, the shared one. Everything below is read off the identity
 * resolveJrStaffIdentities already computed for this dancer, so her own page
 * and the class builder's picker cannot reach different verdicts about her -
 * the exact disease docs/HANDOFF-JR-STAFF-2026-08-29.md was written about.
 *
 * WHICH ACCOUNT CLASSES ATTACH TO is Bobbie's ruling of 2026-09-02, which
 * REVERSED her 2026-08-19 one: "Keep both, email account wins: lists show the
 * email account; the dancer sign-in stays a dancer view." Her linked email
 * account wins whenever one exists; her own sign-in is the fallback, and is
 * what `legacyUids` reports when she has both. Keep both is the half that did
 * not change: nothing is merged or rewritten, the other account is only
 * flagged ("Just flag it, don't touch data").
 */
export function resolveJrStaffDancerPanel(input: {
  studentId: string;
  /** students/{id}.jrStaffDesignated, the server-mirrored ledger flag. */
  designated: boolean;
  users: readonly UserDoc[];
  /** students/{id}.familyUids, for the identity-linkless adult account. */
  familyUids?: readonly string[];
}): JrStaffDancerPanel {
  const empty: JrStaffDancerPanel = {
    isJrStaff: false,
    account: null,
    dancerLogin: null,
    ensembleAccounts: [],
    legacyUids: [],
    possibleEnsembleUids: [],
    blocked: null,
  };
  const studentId = typeof input.studentId === 'string' ? input.studentId.trim() : '';
  if (!studentId) return empty;

  // DOOR TWO, flag-only, asked even for a dancer who turns out not to be Jr
  // Staff at all: a Director still needs to be told there is an account in
  // this family worth linking, and nothing here can act on it either way.
  const possibleEnsembleUids = familyOnlyEnsembleUids(input.users, input.familyUids);

  const identity = jrStaffIdentityForStudent(studentId, {
    users: input.users,
    designations: [{ studentId, designated: input.designated === true, activeAccount: false }],
  });
  // A plain dancer with a sign-in and no designation gets NOTHING, which is
  // the property the old gate's comment was protecting and the one thing
  // this must not lose. A null IDENTITY is that answer; a null ACCOUNT is
  // not, and confusing the two is what hid Emily's panel from her own page.
  if (!identity) return { ...empty, possibleEnsembleUids };

  // DOOR ONE, the identity link (users/{uid}.studentId), proven by the
  // server (dancerClaims.ts). Read back off the identity: the survivor plus
  // every account it displaced, narrowed to the ones actually linked to this
  // dancer. In `input.users` order, so a Director's list reads the way it is
  // stored.
  const linkedUids = new Set<string>([
    ...(identity.accountUid ? [identity.accountUid] : []),
    ...identity.duplicateAccountUids,
  ]);
  // EVERY account the shared resolver saw for this dancer: the survivor and
  // everything it displaced, in `input.users` order so a Director's list
  // reads the way it is stored.
  const linkedAccounts = input.users.filter((u) => linkedUids.has(u.uid));
  const ensembleAccounts = linkedAccounts.filter(
    (u) => u.status === 'active' && u.role === 'ensemble' && u.studentId === studentId
  );

  // HER OWN SIGN-IN, said out loud on her own page whether or not it is the
  // account classes attach to. Since 2026-09-02 it usually is NOT: her linked
  // email account wins and her sign-in "stays a dancer view" (Bobbie). So it
  // is found among ALL of her linked accounts, never read off whichever one
  // happened to win - reading it off the winner is what made it disappear
  // from her page the moment the ruling flipped.
  const dancerLogin = linkedAccounts.find((u) => isLiveDancerLogin(u)) ?? null;
  // Two rival EMAIL accounts and no sign-in of her own is its own state. The
  // shared resolver picks a deterministic winner, because a picker showing
  // the person beats a picker hiding her; her own page refuses to guess out
  // loud instead, because guessing here attaches a class to the wrong
  // account. When she also has a sign-in there is no such deadlock: the
  // email account wins by the ruling and the resolver's pick stands.
  const ambiguous = !dancerLogin && ensembleAccounts.length > 1;
  const account = ambiguous ? null : identity.account;
  // EVERY OTHER ACCOUNT LINKED TO HER, so a class already assigned under one
  // of them keeps reading as assigned and toggling one on cannot put the
  // same human into assistantUids twice. Since the reversal the displaced
  // account is usually her sign-in rather than an older adult account, which
  // is precisely why this reads the identity's own displaced list instead of
  // naming one shape. Identity-linked only, never a possibleEnsembleUid, so
  // toggling a class off can never strip a SIBLING from it.
  const legacyUids = account ? [...identity.duplicateAccountUids] : [];

  // The two honest reasons come straight off the identity's own `gap`, split
  // exactly the way designatedLoginGaps splits them, so this card and the
  // class builder's footer can never describe the same dancer differently.
  const blocked: JrStaffDancerPanel['blocked'] = account
    ? null
    : ambiguous
      ? 'ambiguousAccounts'
      : identity.gap;

  return {
    isJrStaff: true,
    account,
    dancerLogin,
    ensembleAccounts,
    legacyUids,
    possibleEnsembleUids,
    blocked,
  };
}

/**
 * The assistantNames map an offering save should carry for its
 * assistantUids. Names resolve from the CALLER'S list at save time (a
 * Director's list holds every account, dancer logins included); a uid the
 * caller cannot resolve keeps whatever name the stored offering already has
 * rather than losing it, because most staff viewers can never re-derive it
 * (a dancer login is a family-role doc their users query excludes and the
 * rules deny). Uids no longer assigned are dropped, so a removed
 * assistant's name does not linger on the doc.
 *
 * FAMILY-SAFE BY CONSTRUCTION: offerings are readable by every active user
 * and families now render these names (Bobbie, 2026-08-19: "Families need
 * to see the jr staff names as well"), so names come from
 * teacherNameForFamilyCopy - the same builder teacherNames uses - which
 * returns null rather than ever falling back to an account's email address
 * or uid the way teacherDisplayName's display-side chain does. An account
 * with no usable display name simply contributes no snapshot entry.
 */
export function assistantNamesForSave(
  assistantUids: readonly string[],
  resolvableUsers: readonly UserDoc[],
  existing?: Record<string, string> | null
): Record<string, string> {
  const byUid = new Map(resolvableUsers.map((u) => [u.uid, u]));
  const out: Record<string, string> = {};
  for (const uid of assistantUids) {
    if (!uid) continue;
    const person = byUid.get(uid);
    const name = (person ? teacherNameForFamilyCopy(person) : null) ?? existing?.[uid]?.trim();
    if (name) out[uid] = name;
  }
  return out;
}

/** studentIds of a designated dancer, split by WHY they are not on the roster. */
export interface DesignatedLoginGaps {
  /**
   * The family has never created a login at all. Real Jr Staff by the
   * Director's own designation, but nothing yet that could hold a class
   * assignment or an attendance mark until their family links one.
   */
  awaitingLogin: string[];
  /**
   * A login exists and is switched off (dancerLogin.mjs manageLogin
   * disable). The family DID make one; saying "waiting to link a login"
   * about this dancer would be a lie about what actually happened.
   */
  loginDisabled: string[];
}

/**
 * Splits every designated-but-not-currently-usable dancer into the two
 * honest reasons a surface might need to say out loud, so a switched-off
 * login is never described as one that was never made. Surfaces that would
 * otherwise silently drop these dancers can use this to say so honestly.
 *
 * A PROMOTED dancer login counts as a live login here, through the shared
 * isLiveDancerLogin. It has to: the class editor prints this list as "N
 * dancers are assigned Jr Staff and still waiting on their family to make a
 * dancer login" directly under the picker that already lists her, and while
 * these predicates read status 'dancer' as the only dancer-login shape she
 * appeared in both - two contradictory facts on one screen, which is the
 * exact disease the count-and-its-list lesson names.
 *
 * SETTLED BEHAVIOUR, STATED OUT LOUD: a designated dancer whose ONLY account
 * is a linked ACTIVE ensemble account (no dancer login at all) is NOT in
 * either list here. She has an accountUid (the ensemble account, by the
 * resolver's own fallback precedence), so `identity.gap` is null for her -
 * there is nothing to wait on. A pre-resolver version of this split counted
 * her as "waiting on their family to make a dancer login" purely because she
 * had no DANCER login specifically, which was never true: she already has an
 * account her classes attach to.
 */
export function designatedLoginGaps(
  users: readonly UserDoc[],
  designations: readonly JrStaffAssignmentStatus[]
): DesignatedLoginGaps {
  // DERIVED from resolveJrStaffIdentities: an identity with no accountUid IS
  // the gap, and the resolver already decided WHICH gap. Re-deriving the
  // same split here is how the class editor once listed a promoted dancer in
  // its picker and, directly beneath it, counted her as still waiting on a
  // login - two contradictory facts on one screen.
  const awaitingLogin: string[] = [];
  const loginDisabled: string[] = [];
  for (const identity of resolveJrStaffIdentities({ users, designations })) {
    if (!identity.studentId || identity.accountUid) continue;
    if (identity.gap === 'loginDisabled') loginDisabled.push(identity.studentId);
    else awaitingLogin.push(identity.studentId);
  }
  awaitingLogin.sort((a, b) => a.localeCompare(b));
  loginDisabled.sort((a, b) => a.localeCompare(b));
  return { awaitingLogin, loginDisabled };
}

/** A guardian standing in for a Jr Staff dancer a message cannot reach. */
export interface JrStaffGuardianStandIn {
  studentId: string;
  /**
   * Why the dancer herself is not written to:
   *   - 'awaitingLogin': the family never made a dancer sign-in.
   *   - 'loginDisabled': a sign-in exists and is switched off.
   *   - 'notPromoted': a LIVE dancer sign-in exists, but a dancer sign-in is
   *     a server cookie session with no Firebase Auth principal (see
   *     jrStaffDancerSession.ts), so nothing is ever signed in as that uid to
   *     open a thread addressed to it, and the dancer portal reads only its
   *     own dancer-initiated threads (dancerStaffThreads.mjs filters to
   *     `dancerStaffThread === true`). Until the Jr Staff parity build gives
   *     these dancers a real staff session, a message "to" that uid would
   *     succeed and be read by nobody - so it goes to her guardians instead,
   *     named honestly as a different fact than a login that was never made.
   *
   * A caller whose users list cannot include dancer sign-in docs (staff
   * directory reads exclude status 'dancer' for non-Directors) will see
   * 'awaitingLogin' where a Director sees 'notPromoted'. The RECIPIENTS are
   * identical either way - both cases route to guardians - only the reason
   * label degrades, so copy shown to non-Directors should not lean on the
   * distinction.
   */
  reason: 'awaitingLogin' | 'loginDisabled' | 'notPromoted';
  /** The guardian accounts a message reaches instead. Empty means nobody. */
  guardianUids: string[];
}

export interface JrStaffMessageAudience {
  /**
   * Jr Staff written to directly: EVERY ACTIVE SIGN-IN each of them holds.
   *
   * Bobbie, 2026-09-07, verbatim: *"Everything should reach both signs they
   * should act exactly the same, and parent should be copied on all of the
   * junior staff correspondence."* Her 2026-09-02 ruling that "the email
   * account wins" governs LISTS - a roster, a picker, a class card, one entry
   * per human - and does NOT govern delivery. A message to Jr Staff reaches
   * both of her accounts, exactly as jrStaffMentionRecipientUids has always
   * done for an @mention.
   *
   * ONLY A PROMOTED SIGN-IN IS ADDED, and the qualifier is load bearing. A
   * second uid earns a seat here only when it is an ACTIVE 'ensemble'
   * account, the same test the mention helper applies - which in practice
   * means a PROMOTED dancer sign-in, because promotion is what rewrites that
   * doc to active/'ensemble'. An UNPROMOTED dancer login is a server cookie
   * session with no Firebase principal that could ever open a thread: it gets
   * no seat, and keeps the guardian stand-in below instead of a seat that
   * reaches nobody. "Both sign-ins" is therefore a claim about a linked pair
   * where the dancer half has been promoted, never about every dancer login.
   *
   * WHAT THIS COSTS, said out loud. /messages/new hands this list to a group
   * thread's participantUids, so a linked pair spends TWO of the hundred
   * seats (lib/threadLimits.ts) and the thread becomes readable on both of
   * her sign-ins. That second half is the point: shared/firestore.rules gates
   * a thread on `request.auth.uid in resource.data.participantUids`, so
   * naming both uids is exactly what makes one group message reach both
   * signs with no rules change at all.
   *
   * IT APPLIES TO A NEW THREAD ONLY. A group thread created before the pair
   * was linked, and every DM, carries ONE uid and stays readable on that uid
   * alone - nothing here rewrites an existing participantUids, and doing so
   * would be granting read access by growing a rules-checked array. See
   * useFindOrCreateDm in lib/threads.ts for how a DM is kept to one
   * conversation instead of split across two.
   */
  directUids: string[];
  /** Guardians written to because the Jr Staff themselves cannot be. */
  guardianUids: string[];
  /** Every uid the message goes to, de-duplicated, direct first. */
  recipientUids: string[];
  /** One row per dancer reached through a parent, so a screen can say so. */
  standIns: JrStaffGuardianStandIn[];
  /** Designated dancers with no login AND no guardian account: unreachable. */
  unreachableStudentIds: string[];
}

/**
 * WHO A MESSAGE TO "JR STAFF" ACTUALLY REACHES.
 *
 * Bobbie, 2026-08-29, after Kim found the Staff page offering 16 Jr Staff
 * while every family record showed more of them marked: *"It needs to fix it
 * once for all that any dancer who has a true account or a dancer jr staff
 * designation is labeled and contactable via jr that message."* And, in the
 * same breath, what to do about the ones with no account: *"if they didn't
 * create a dancer account then she needs to be able to email their parents in
 * the group."*
 *
 * BEING JR STAFF AND BEING REACHABLE ARE DIFFERENT QUESTIONS, and this file
 * already answered the first one. resolveJrStaffUsers is the roster; this adds
 * only the second. The message group used to ask neither: it filtered users to
 * role 'ensemble', so a dancer the Director had marked but whose family never
 * made her a sign-in was marked everywhere a Director looked and present in
 * nothing a Director could send to.
 *
 * Direct recipients come from the same identity resolver as the roster,
 * filtered by hasFirebaseSession. A promoted dancer sign-in has a real
 * Firebase principal; an unpromoted cookie-only login does not. When both
 * live accounts are linked, the email account wins, including for message
 * delivery. Every designated dancer not covered by a reachable account
 * keeps the existing guardian fallback and its visible reason.
 *
 * The guardian is a STAND-IN, never a substitution. Every parent written to
 * this way is reported in `standIns` with the reason, because a group called
 * Jr Staff that quietly contains parents changes what that name means for
 * everyone who later reads the thread - the exact objection behind the
 * existing rule that a parent cannot be dropped into a saved staff group
 * (newMessage/recipientRules.ts). Saying it out loud is what makes this
 * different from that.
 *
 * A dancer with no login and no guardian account is returned in
 * `unreachableStudentIds` rather than dropped. Dropping her would rebuild the
 * original bug one layer down: a confident list that silently omits somebody
 * the Director marked.
 */
export function jrStaffMessageAudience(input: {
  users: readonly UserDoc[];
  designations: readonly JrStaffAssignmentStatus[];
  /** Guardian uids per student, from the students already loaded by the caller. */
  guardianUidsByStudent: ReadonlyMap<string, readonly string[]>;
  /** Never write to yourself. */
  excludeUid?: string | null;
  /**
   * What the caller's `users` list actually contains, so a guardian uid
   * ABSENT from it can be judged honestly. Threads and their messages
   * require an ACTIVE account to read (shared/firestore.rules,
   * isActiveUserByDoc), so a paused or deleted guardian written into a
   * thread is the same silent non-delivery this audience exists to stop.
   *   - 'allUsers': every users doc (a Director's directory). Absence means
   *     no account exists at all - drop the uid.
   *   - 'activeUsers': every ACTIVE user (manager/foh directory). Absence
   *     means the account is not active - drop the uid.
   *   - 'activeStaffOnly' (default): active staff only (plain staff
   *     directory). A guardian's absence proves nothing - keep the uid,
   *     this caller has no authority to rule it out.
   * A guardian PRESENT in `users` with a non-active status is dropped under
   * every scope. A dancer whose guardians all drop is unreachable, reported,
   * never silently "sent to".
   */
  directoryScope?: 'allUsers' | 'activeUsers' | 'activeStaffOnly';
}): JrStaffMessageAudience {
  const excluded = typeof input.excludeUid === 'string' ? input.excludeUid.trim() : '';
  const keep = (uid: string) => uid.length > 0 && uid !== excluded;
  const scope = input.directoryScope ?? 'activeStaffOnly';
  const usersByUid = new Map(input.users.map((u) => [u.uid, u]));
  const guardianCanReadThreads = (uid: string): boolean => {
    const doc = usersByUid.get(uid);
    if (doc) return doc.status === 'active';
    return scope === 'activeStaffOnly';
  };

  // One real Firebase recipient per resolved human. Both linked accounts
  // remain live; the email account wins delivery just as it wins the roster.
  const identities = resolveJrStaffIdentities({
    users: input.users, designations: input.designations,
    familyUidsByStudent: input.guardianUidsByStudent,
  }).filter((identity) => identity.hasFirebaseSession && identity.accountUid);
  // Losing the display tie does not turn an active staff login into a parent.
  const activeEnsembleUids = new Set(input.users.filter(user => user.role === 'ensemble' && user.status === 'active').map(user => user.uid));
  const directUids = identities
    .filter((identity) => identity.accountUid !== excluded && !identity.duplicateAccountUids.includes(excluded))
    // BOTH SIGN-INS (Bobbie, 2026-09-07: "Everything should reach both signs
    // they should act exactly the same"). The winner of the display tie is
    // still first, so a reader of the recipient list meets her under the name
    // every other list uses; her other live account follows it rather than
    // replacing it. Only an ACTIVE ensemble account qualifies, the same gate
    // jrStaffMentionRecipientUids applies, because an unpromoted dancer login
    // has no Firebase principal to receive anything with.
    .flatMap((identity) => [
      identity.accountUid!,
      ...identity.duplicateAccountUids.filter((uid) => activeEnsembleUids.has(uid)),
    ])
    .filter(keep);
  // A sender also covers her own linked dancer, so do not add a guardian
  // stand-in merely because her other sign-in was excluded above.
  const ensembleCoveredStudentIds = new Set(
    identities.map((identity) => identity.studentId).filter((id): id is string => !!id)
  );

  const liveLoginStudentIds = new Set(
    input.users.filter(isLiveDancerLogin).map((u) => u.studentId).filter((id): id is string => !!id)
  );
  const anyLoginStudentIds = new Set(
    input.users.filter(isDancerLoginShapedUser).map((u) => u.studentId).filter((id): id is string => !!id)
  );

  const standIns: JrStaffGuardianStandIn[] = [];
  const unreachableStudentIds: string[] = [];
  const guardianUids: string[] = [];

  const consider = (studentId: string, reason: JrStaffGuardianStandIn['reason']) => {
    const raw = (input.guardianUidsByStudent.get(studentId) ?? [])
      .map((uid) => (typeof uid === 'string' ? uid.trim() : ''))
      .filter((uid) => uid.length > 0);
    const guardians = [...new Set(raw.filter((uid) => uid !== excluded).filter(guardianCanReadThreads))];
    if (guardians.length === 0) {
      // A guardian list emptied ONLY by excluding the sender is not an
      // unreachable dancer: the one person who may see her is the person
      // composing the message. Reached by definition, reported as neither.
      if (raw.some((uid) => uid === excluded)) return;
      unreachableStudentIds.push(studentId);
      return;
    }
    standIns.push({ studentId, reason, guardianUids: guardians });
    for (const uid of guardians) guardianUids.push(uid);
  };
  const designatedStudentIds = [
    ...new Set(input.designations.filter((s) => s.designated).map((s) => s.studentId)),
  ].sort((a, b) => a.localeCompare(b));
  for (const studentId of designatedStudentIds) {
    if (ensembleCoveredStudentIds.has(studentId)) continue;
    // An ACTIVE ensemble account sitting in this dancer's own familyUids is
    // already a direct recipient. In production that shape is overwhelmingly
    // the dancer's own staff account missing its users.studentId identity
    // link (the identity-link lane grants family view; ~2 dozen accounts
    // predate it) - reporting her as "reached through a parent" would call
    // her own account her parent and double-count one human. A genuine
    // staff parent is covered by the same logic: the message reaches that
    // household directly either way.
    const guardianList = input.guardianUidsByStudent.get(studentId) ?? [];
    if (guardianList.some((uid) => typeof uid === 'string' && activeEnsembleUids.has(uid.trim()))) {
      continue;
    }
    if (liveLoginStudentIds.has(studentId)) {
      consider(studentId, 'notPromoted');
    } else if (anyLoginStudentIds.has(studentId)) {
      consider(studentId, 'loginDisabled');
    } else {
      consider(studentId, 'awaitingLogin');
    }
  }

  // Direct first, so a reader of the recipient list meets the Jr Staff
  // themselves before the parents standing in for the rest. A guardian who is
  // ALSO staff appears once, under whichever came first.
  const recipientUids = [...new Set([...directUids, ...guardianUids])];
  const guardianOnlyUids = recipientUids.filter((uid) => !directUids.includes(uid));

  standIns.sort((a, b) => a.studentId.localeCompare(b.studentId));
  unreachableStudentIds.sort((a, b) => a.localeCompare(b));

  return {
    directUids,
    guardianUids: guardianOnlyUids,
    recipientUids,
    standIns,
    unreachableStudentIds,
  };
}

// ---------------------------------------------------------------------------
// ONE RESOLVER, THREE QUESTIONS
// ---------------------------------------------------------------------------

/**
 * THE ANSWER TO ALL THREE JR STAFF QUESTIONS, IN ONE PLACE.
 *
 * Bobbie, 2026-09-01, told that a designated Jr Staff was missing from every
 * picker: *"THIS IS NOT the first time this issue has come up. Parker is
 * another."* She is right. In one evening the SAME root cause produced six
 * separately-filed bugs: a Jr assistant rendered as a hex serial number, a
 * designated Jr Staff in no picker anywhere, the class builder's Jr Staff
 * dropdown listing five humans twice each, the schedule grid printing one
 * person twice, two attendance rows both reading "Someone", and a dancer's
 * own page refusing to draw her Jr Staff panel. Every one of them was a
 * surface answering one of three questions for itself:
 *
 *   a. Is this person Jr Staff?
 *   b. Which account do their classes, attendance and media attach to?
 *   c. What name do we show for them?
 *
 * A shared definition already existed above and the surfaces did not read
 * it. That finding is four days old (docs/HANDOFF-JR-STAFF-2026-08-29.md
 * section 4) and has now been re-proved five times. So this resolver answers
 * all three at once, per HUMAN rather than per account, and every surface
 * reads the ANSWER instead of the raw `users` list.
 *
 * THE SETTLED RULES IT ENCODES, all Bobbie's, none re-litigated here:
 *
 *   - *"if the dancer login and their flagged as junior staff, or they have
 *     their own account and they are flagged to junior staff, they should be
 *     behaving the exact same way."* One roster, three populations, no
 *     branch that quietly drops one of them.
 *   - 2026-09-02, REVERSING her 2026-08-19 ruling: *"Keep both, email account
 *     wins: lists show the email account; the dancer sign-in stays a dancer
 *     view."* Her linked email account WINS question (b); her own sign-in is
 *     the fallback, and is what gets reported as displaced. Until 2026-09-02
 *     this was the other way round (*"Only the dancer has classes assigned so
 *     that IS the correct person"*), so a comment anywhere in this repo that
 *     still says the login wins is stale, not a second opinion.
 *   - *"Just flag it, don't touch data."* A second account for the same
 *     human is REPORTED in duplicateAccountUids and left exactly as it is.
 *   - A plain dancer with a sign-in and no designation is not Jr Staff.
 *     Having a login is not being Jr Staff.
 *   - *"on everything teachers only want first name not last."* The name
 *     comes from teacherDisplayName, which can never return a uid or an
 *     email address (lib/teacherTitle.ts).
 *
 * WHAT IT DOES NOT DECIDE. Being Jr Staff and being able to RECEIVE a
 * Firebase-authenticated write are different questions, and collapsing them
 * is how the messaging bug in section 3 of that handoff happened. Every
 * identity therefore carries `hasFirebaseSession`, and a surface that needs
 * one FILTERS this list rather than re-deriving membership from
 * `role === 'ensemble'` on its own. The filter is visible; the re-derivation
 * was not, and that is the whole difference.
 */
export interface JrStaffIdentity {
  /**
   * The dancer behind this Jr Staff, or null for a staff account with no
   * server-written identity link (users/{uid}.studentId).
   */
  studentId: string | null;
  /**
   * QUESTION (b). The account classes, attendance and media attach to, by
   * the precedence above: her linked email account first, her own dancer
   * sign-in second, and null for a designated dancer with no account of any
   * kind. Null is an honest answer, never a reason to hide the person.
   *
   * That order REVERSED on 2026-09-02 (Bobbie: "lists show the email
   * account"); it read sign-in first until then.
   */
  accountUid: string | null;
  /** The same account as a doc, for a caller that needs more than the uid. */
  account: UserDoc | null;
  /**
   * QUESTION (c). Never a uid, never an email address: teacherDisplayName
   * refuses both, and a person with no readable name anywhere resolves to
   * the one honest placeholder (UNNAMED_PERSON_LABEL).
   */
  displayName: string;
  /**
   * True only when a real Firebase Auth principal can sign in as
   * `accountUid`: an ACTIVE ensemble account, a PROMOTED dancer login
   * included, because she is one.
   *
   * JR_STAFF_DANCER_PARITY is on in production as of 2026-09-02, and that is
   * the whole reason promotion is not an exception here. mintJrStaffToken in
   * netlify/functions/dancerAuth.mjs mints a Firebase custom token on the
   * SAME uid for a promoted login, the client signs in with it, and the
   * rules then judge her by her users doc exactly like any other staff
   * member (isActiveStaffByDoc in shared/firestore.rules, whose role list
   * carries 'ensemble'). Excluding her here would have hidden her from every
   * award, thread and announcement surface that filters on this flag, for
   * picks she can in fact cast.
   *
   * An UNPROMOTED dancer sign-in is still an HttpOnly cookie session with no
   * Firebase principal at all, so anything gated on `request.auth` genuinely
   * cannot reach it, and it is still false here. Filter on this; never
   * re-derive it.
   */
  hasFirebaseSession: boolean;
  /**
   * Why there is no account, when there is none. Two honest reasons, never
   * collapsed: 'awaitingLogin' means the family never made one,
   * 'loginDisabled' means one exists and is switched off.
   */
  gap: 'awaitingLogin' | 'loginDisabled' | null;
  /**
   * Every OTHER account belonging to this same human, flag-only. This is
   * what a surface says out loud instead of either shrinking by one silently
   * or printing the same person twice. Covers every shape: an ensemble
   * account displaced by a dancer login, a SECOND active ensemble account
   * with no login at all, and a second live dancer login on the same
   * studentId - a shape that should not exist by design but did, and used to
   * vanish here with nothing to say it was ever there.
   */
  duplicateAccountUids: string[];
  /**
   * DOOR TWO, and it is a different fact from duplicateAccountUids above.
   * Active ensemble accounts sitting in this dancer's familyUids with no
   * identity link of their own: a POSSIBLE same person, never a proven one,
   * because a sibling's account wears that shape identically (see
   * familyOnlyEnsembleUids). Nothing is collapsed, removed or displaced for
   * these; they stay on the roster as their own identities, and a surface
   * can only tell a Director there is something to confirm in Manage Users.
   *
   * Empty unless the caller passed `familyUidsByStudent`, and empty for a
   * dancer with no live sign-in of her own - a match nothing can be named
   * against. Each uid is claimed by at most one dancer, the first in
   * studentId order, so two dancers never both flag the same account.
   */
  possibleDuplicateUids: string[];
}

/** What every Jr Staff question is answered from. */
export interface JrStaffSource {
  users: readonly UserDoc[];
  designations: readonly JrStaffAssignmentStatus[];
  /**
   * students/{id}.familyUids per dancer, the SECOND door onto "these two
   * accounts might be one human" (see familyOnlyEnsembleUids). Optional: a
   * caller that cannot load students simply gets no possible-match flags.
   * It only ever ADDS a flag - it never changes who is on the roster, which
   * account a class attaches to, or what anyone is called.
   */
  familyUidsByStudent?: FamilyUidsByStudent;
}

/**
 * Adapt the server-mirrored students.jrStaffDesignated flag into designation
 * rows, so a surface holding students (every staff reader of /students) can
 * answer question (a) without the Director-only designation ledger and
 * without inventing its own shape for the same fact. NewMessage.tsx built
 * this inline first; it lives here so the next caller cannot spell it
 * differently.
 */
export function designationsFromStudents(
  students: readonly { id: string; jrStaffDesignated?: boolean }[]
): JrStaffAssignmentStatus[] {
  return students.map((s) => ({
    studentId: s.id,
    designated: s.jrStaffDesignated === true,
    activeAccount: false,
  }));
}

/**
 * Every Jr Staff at the studio, ONE ENTRY PER HUMAN, with all three
 * questions answered. Alphabetical by the name shown, so every caller gets
 * the same order without re-sorting.
 *
 * `studentNamesById` is optional and is the ONLY way a designated dancer
 * with no account of any kind gets a real name: her name lives on the
 * student doc, not on any users doc. A caller that does not hold students
 * gets the honest placeholder rather than an invented label - the gap stays
 * visible, which is the golden rule applied to names.
 */
export function resolveJrStaffIdentities(
  source: JrStaffSource,
  studentNamesById?: ReadonlyMap<string, string>
): JrStaffIdentity[] {
  const { users, designations, familyUidsByStudent } = source;
  const activeEnsemble = users.filter((u) => u.status === 'active' && u.role === 'ensemble');

  const designatedStudentIds = new Set(
    designations.filter((status) => status.designated).map((status) => status.studentId)
  );
  // A dancer server-linked to an ACTIVE ensemble account is Jr Staff by that
  // role itself - the Director gave the person the role, and "there are no
  // jr staff who are not also on a roster as a dancer" (Bobbie, 2026-08-19).
  // Treated exactly like a ledger designation, which is what stops this file
  // and a dancer's own page reaching different verdicts about one person.
  const ensembleLinkedStudentIds = new Set(
    activeEnsemble
      .map((u) => u.studentId)
      .filter((id): id is string => typeof id === 'string' && id.length > 0)
  );

  const anyLoginStudentIds = new Set(
    users.filter(isDancerLoginShapedUser).map((u) => u.studentId).filter((id): id is string => !!id)
  );

  const studentIds = [...new Set([...designatedStudentIds, ...ensembleLinkedStudentIds])].sort(
    (a, b) => a.localeCompare(b)
  );

  const identities: JrStaffIdentity[] = [];
  const claimedUids = new Set<string>();
  // Door two claims an account for at most ONE dancer, the first in
  // studentId order, so an account sitting in two families' lists is never
  // flagged twice under two different names.
  const flaggedPossibleUids = new Set<string>();

  for (const studentId of studentIds) {
    // Sorted by uid throughout, the same tiebreak every other pass in this
    // file uses, so two functions can never name a different winner for the
    // same human.
    const forStudent = [...users]
      .filter((u) => u.studentId === studentId)
      .sort((a, b) => a.uid.localeCompare(b.uid));
    const liveLogins = forStudent.filter((u) => isLiveDancerLogin(u));
    const login = liveLogins[0] ?? null;
    // A SECOND live dancer login on the same studentId (a data shape that
    // should not exist by design, but did: roster: ["login-a"] duplicates:
    // [] before this fix). Neither role='family' nor a promoted uid that
    // is not `login` matched ensembleAccounts below, so it was dropped
    // everywhere - not flagged, not even counted, unlike an ensemble
    // duplicate. It is now reported exactly like one.
    // HER EMAIL ACCOUNTS: active ensemble accounts that are not a dancer
    // sign-in at all. EVERY live login is excluded here, not just the first
    // one (review finding F7): a PROMOTED login is both a live login and an
    // active ensemble account, so excluding only the winner matched her in
    // both lists and put her uid into duplicateAccountUids TWICE, which the
    // Offerings footer counts - it over-reported the very doubles it exists
    // to report.
    const emailAccounts = jrStaffEmailAccounts(forStudent, studentId);

    // THE EMAIL ACCOUNT WINS. Bobbie, 2026-09-02, asked what should happen to
    // the older email account when a Director links it to a dancer who also
    // has her own sign-in: *"Keep both, email account wins: lists show the
    // email account; the dancer sign-in stays a dancer view."*
    //
    // THIS REVERSES HER 2026-08-19 RULING, which said the opposite ("Only the
    // dancer has classes assigned so that IS the correct person") and which
    // this file encoded as `login ?? emailAccounts[0]` until tonight. Keep
    // both is unchanged - nothing is merged, deleted or rewritten, the
    // displaced account is only REPORTED - but the winner flipped, so the
    // roster, every picker, every card and every class attachment now name
    // the email account's uid.
    //
    // A dancer whose ONLY account is her sign-in is untouched by this: she is
    // the sole account and wins by default, promoted or not.
    const account = emailAccounts[0] ?? login ?? null;
    const duplicateAccountUids = [
      ...emailAccounts.filter((u) => u.uid !== account?.uid).map((u) => u.uid),
      ...liveLogins.filter((u) => u.uid !== account?.uid).map((u) => u.uid),
    ];
    if (account) claimedUids.add(account.uid);
    for (const uid of duplicateAccountUids) claimedUids.add(uid);
    // DOOR TWO, flag-only. Only a dancer whose OWN live sign-in is the
    // account can carry these: a possible match has to be named against
    // something, and collapsing toward a login this roster does not hold
    // would delete a person from the picker rather than flag her.
    const possibleDuplicateUids = login
      ? familyOnlyEnsembleUids(users, familyUidsByStudent?.get(studentId)).filter(
          (uid) => !flaggedPossibleUids.has(uid)
        )
      : [];
    for (const uid of possibleDuplicateUids) flaggedPossibleUids.add(uid);
    identities.push({
      studentId,
      accountUid: account?.uid ?? null,
      account,
      displayName: account
        ? teacherDisplayName(account)
        : studentNamesById?.get(studentId)?.trim() || UNNAMED_PERSON_LABEL,
      // A PROMOTED dancer login wears the same role/status shape as a real
      // ensemble account (dancerLoginUserState in
      // netlify/functions/lib/jrStaffDancerBridge.mjs writes role 'ensemble',
      // status 'active' on promotion) AND holds a real Firebase principal to
      // match it, so the shape is not a lie and there is nothing to subtract
      // here. dancerAuth.mjs mints a custom token on her own uid under
      // JR_STAFF_DANCER_PARITY, on in production as of 2026-09-02, and the
      // rules judge her by her users doc like anyone else (isActiveStaffByDoc,
      // shared/firestore.rules). An earlier pass excluded her from this flag
      // and would have hidden her from every surface that filters on it.
      hasFirebaseSession:
        !!account && account.status === 'active' && account.role === 'ensemble',
      gap: account ? null : anyLoginStudentIds.has(studentId) ? 'loginDisabled' : 'awaitingLogin',
      duplicateAccountUids,
      possibleDuplicateUids,
    });
  }

  // An ACTIVE ensemble account with NO identity link is still Jr Staff: the
  // Director gave it the role. Roughly two dozen accounts predate the
  // identity-link lane and carry no users/{uid}.studentId at all, and
  // dropping them here would rebuild the original bug one layer down.
  for (const u of activeEnsemble) {
    if (claimedUids.has(u.uid)) continue;
    if (typeof u.studentId === 'string' && u.studentId.length > 0) continue;
    identities.push({
      studentId: null,
      accountUid: u.uid,
      account: u,
      displayName: teacherDisplayName(u),
      hasFirebaseSession: true,
      gap: null,
      duplicateAccountUids: [],
      possibleDuplicateUids: [],
    });
  }

  return identities.sort(
    (a, b) =>
      a.displayName.localeCompare(b.displayName)
      || (a.accountUid ?? '').localeCompare(b.accountUid ?? '')
      || (a.studentId ?? '').localeCompare(b.studentId ?? '')
  );
}

/**
 * QUESTION (a) AND (b) FOR ONE DANCER. Null means she is not Jr Staff at
 * all; an identity whose `accountUid` is null means she IS Jr Staff and has
 * no account yet, which is a completely different fact and must render
 * differently. A surface that returns early on a missing ACCOUNT rather than
 * a missing IDENTITY is the exact bug that hid a designated Jr Staff's own
 * panel from her own page.
 */
export function jrStaffIdentityForStudent(
  studentId: string,
  source: JrStaffSource,
  studentNamesById?: ReadonlyMap<string, string>
): JrStaffIdentity | null {
  if (!studentId) return null;
  return (
    resolveJrStaffIdentities(source, studentNamesById).find((i) => i.studentId === studentId) ?? null
  );
}

/** QUESTION (a), on its own, for one dancer. */
export function isDancerJrStaff(studentId: string, source: JrStaffSource): boolean {
  return jrStaffIdentityForStudent(studentId, source) !== null;
}

/**
 * ONE HUMAN, ONE ENTRY, applied to a list of uids a class already stores.
 *
 * The schedule grid printed one person twice because a class held her adult
 * ensemble account as lead and her dancer sign-in as a co-teacher: two uids,
 * two lookups, two identical names on one tile. De-duplicating by uid cannot
 * see that, and de-duplicating by NAME would silently merge two different
 * people who happen to share a first name. So it collapses by the identity
 * link the SERVER wrote (users/{uid}.studentId), the same fact question (b)
 * is decided by, keeping whichever uid that precedence prefers.
 *
 * Order is preserved: the surviving uid keeps the position of the FIRST of
 * the pair, so a lead teacher stays first on the tile.
 */
export function oneEntryPerHuman(
  uids: readonly string[],
  users: readonly UserDoc[]
): string[] {
  const byUid = new Map(users.map((u) => [u.uid, u]));
  const out: string[] = [];
  const seenStudentIds = new Map<string, number>();
  for (const uid of uids) {
    const person = byUid.get(uid);
    const studentId = typeof person?.studentId === 'string' ? person.studentId : '';
    // ONLY HER OWN SIGN-INS COLLAPSE, never everybody sharing her studentId.
    // setDancerLink.mjs writes studentId onto a GUARDIAN's account as well, so
    // the plain studentId test here folded a mother and her Jr Staff daughter
    // into ONE entry: the people picker offered a single chip and the daughter
    // could not be messaged at all, and a board roster dropped the guardian
    // member the same way. isOwnJrStaffSignIn (lib/jrStaffAliases.ts) is the
    // one guard, shared with the canonical uid and the server exclusion.
    if (!studentId || !isOwnJrStaffSignIn(person)) {
      out.push(uid);
      continue;
    }
    const at = seenStudentIds.get(studentId);
    if (at === undefined) {
      seenStudentIds.set(studentId, out.length);
      out.push(uid);
      continue;
    }
    // Already holding this human. Keep the account question (b) prefers:
    // her linked EMAIL account, else whichever came first. Bobbie, 2026-09-02
    // ("lists show the email account"), reversing 2026-08-19 - this preferred
    // her sign-in until tonight.
    const heldUid = out[at];
    const held = heldUid === undefined ? undefined : byUid.get(heldUid);
    // The tiebreak is jrStaffAliasCandidateWins (./jrStaffAliases.ts), shared
    // with assistantNamesForOfferingData in netlify/functions/dancerPortal.mjs
    // so one ruling has one spelling on both sides of the app.
    if (held && person && jrStaffAliasCandidateWins(
      isLiveDancerLogin(held), isLiveDancerLogin(person)
    )) out[at] = uid;
  }
  return out;
}

/**
 * THE OTHER HUMANS IN A THREAD, FROM THIS VIEWER'S SEAT (Packet J32 finding
 * 2, Fable's refute round on PR #1141).
 *
 * A merged DM thread's participantUids can now hold BOTH of a Jr Staff
 * viewer's own uids (dmForJrStaff.mjs), and - since finding 3 of the same
 * round - both uids of a Jr Staff SHE is messaging too. Every surface that
 * used to compute "everyone else" as `participantUids.filter(uid => uid !==
 * meUid)` therefore printed the viewer's OWN other sign-in as if it were a
 * second person: a DM with Kim titled itself with the viewer's own name, a
 * task-assignee sheet offered "assign to yourself" wearing a stranger's
 * face, and a two-person conversation read as a 3-person group.
 *
 * This removes the VIEWER'S ENTIRE alias set first (jrStaffAliasSets, the
 * same "every uid to every uid of the same human" answer #1049's report and
 * useFindOrCreateDm already share - never a second definition), THEN
 * collapses whatever remains to one entry per human (oneEntryPerHuman
 * above), so a Jr Staff on the OTHER end of the same conversation who also
 * holds two uids still prints once. Each surviving entry is finally resolved
 * to its owner's CANONICAL (email-preferred) uid - canonicalJrStaffAliasUid,
 * the same "lists show the email account" precedence every other DM list and
 * picker already uses - so avatar, title and profile-button target agree
 * with every other surface regardless of which of her two uids happened to
 * sort first inside participantUids.
 *
 * A plain 2-uid DM (no aliasing on either side, still the overwhelming
 * majority) is unaffected: removing the viewer's singleton alias set and
 * collapsing a one-person remainder is exactly today's
 * `participantUids.filter(uid => uid !== meUid)`.
 */
export function threadOtherHumanUids(
  participantUids: readonly string[],
  viewerUid: string,
  users: readonly UserDoc[]
): string[] {
  const aliasSets = jrStaffAliasSets(users);
  const mySet = new Set(aliasSets.get(viewerUid) ?? [viewerUid]);
  const remaining = participantUids.filter((uid) => !mySet.has(uid));
  return oneEntryPerHuman(remaining, users).map(
    (uid) => canonicalJrStaffAliasUid(uid, users) || uid
  );
}

/**
 * ONE HUMAN, ONE ENTRY, FOR A READER WHO CANNOT SEE ACCOUNTS AT ALL.
 *
 * oneEntryPerHuman above collapses by the identity link the server wrote,
 * which is the right answer and needs the two user docs to read it from. A
 * FAMILY viewer holds neither: useUsersList returns `[userDoc]`, the viewer's
 * own document and nothing else (src/lib/users.ts - a family may not read
 * staff docs, and the rules say so). So on a family screen every assistant
 * uid resolves to nothing, oneEntryPerHuman collapses nothing, and the double
 * Bobbie photographed survived on the one screen she was looking at. That is
 * review finding F2.
 *
 * The only signal a family CAN read is the offering's own stored name
 * snapshot, so that is what this collapses: identical rendered labels, first
 * one wins, order preserved.
 *
 * WHY THAT IS SAFE HERE AND NOWHERE ELSE. Collapsing by label would merge two
 * genuinely different humans who share a first name - which is exactly why
 * dedupedStaffNames spends a last initial to keep them apart. But that
 * initial is a staff-only affordance by Bobbie's naming rule ("Ms Dallas not
 * Dallas Brown anywhere"), and the reason it is staff-only is the reason this
 * is safe: a family reading their own dancer's class page never needs to tell
 * two Ms. Ellas apart. Two identical names on one class is one line to them
 * either way. So a family gets one honest "Ms. Ella" instead of a name
 * printed twice, and still never sees a surname or an initial.
 *
 * STAFF MUST NOT CALL THIS. A Director staffing a class does need to know
 * there are two people; for her the live accounts are readable, so
 * oneEntryPerHuman and dedupedStaffNames answer it properly.
 */
/**
 * EVERY UID OF ONE HUMAN, POINTING AT THE ONE THE LISTS SHOW.
 *
 * Bobbie's acceptance criterion, verbatim, 2026-09-02: *"the updating of the
 * dropdown to make sure no one who is already assigned a jr assistant classes
 * loses what they have been assigned, but also we stop getting duplicates."*
 * Both halves, and the first one is the dangerous half.
 *
 * COLLAPSING TWO ACCOUNTS INTO ONE ENTRY IS A DISPLAY RULE, NEVER A DATA RULE.
 * Nothing in this file removes a uid from an offering. But a picker that only
 * recognises the SURVIVING uid quietly breaks the promise anyway: a class
 * already holding her other uid renders as "no longer Jr Staff" and the next
 * save writes that back, so a Director watching the screen sees an assignment
 * she still has being described as lost. That became a live risk the moment
 * the winner flipped on 2026-09-02, because every class assigned under the old
 * ruling holds her DANCER SIGN-IN's uid, and the winner is now her email
 * account.
 *
 * So this returns the whole human: any uid she holds maps to the uid the lists
 * show. A surface uses it to recognise a stored assignment under EITHER uid as
 * the same person, to add under the winning uid, and to remove every uid of
 * hers together, so one human can never be half-attached.
 *
 * Only IDENTITY-LINKED accounts are grouped (users/{uid}.studentId, written by
 * the server). A possible-duplicate flag never groups anybody: a sibling's
 * account wears that shape identically, and grouping it would let one tap
 * strip a different human off her own class.
 */
export function jrStaffUidAliases(
  source: JrStaffSource,
  studentNamesById?: ReadonlyMap<string, string>
): Map<string, string> {
  const aliases = new Map<string, string>();
  for (const identity of resolveJrStaffIdentities(source, studentNamesById)) {
    const primary = identity.accountUid;
    if (!primary) continue;
    aliases.set(primary, primary);
    for (const uid of identity.duplicateAccountUids) aliases.set(uid, primary);
  }
  return aliases;
}

/** A list has one winner, but a mention reaches every active sign-in for that person. */
export function jrStaffMentionRecipientUids(users: UserDoc[], excludeUid: string): string[] {
  const recipients = new Set<string>();
  for (const identity of resolveJrStaffIdentities({ users, designations: [] })) {
    const accounts = [identity.accountUid, ...identity.duplicateAccountUids].filter((uid): uid is string => Boolean(uid));
    if (accounts.includes(excludeUid)) continue;
    for (const uid of accounts) {
      if (users.some(user => user.uid === uid && user.role === 'ensemble' && user.status === 'active')) recipients.add(uid);
    }
  }
  return [...recipients];
}

export function oneEntryPerFamilyLabel(names: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const name of names) {
    const label = (name ?? '').trim();
    // An empty label is not a name and never collapses onto another one.
    if (!label) continue;
    if (seen.has(label)) continue;
    seen.add(label);
    out.push(label);
  }
  return out;
}

/**
 * Last-initial-only, STAFF SURFACES ONLY, never a family-facing rendering.
 * Bobbie's naming rule is first-name-only ("Ms Dallas not Dallas Brown
 * anywhere") because a family reading their own dancer's class page never
 * needs to tell two Ellas apart - there is one teacher of record on that
 * class. A Director staffing from a list of forty Jr Staff is a different
 * reader with a different need: when two of them share a first name she has
 * to be able to tell which is which. Every caller of dedupedStaffNames below
 * is a Director- or staff-only surface; see multiple-executive-teachers.test.mjs
 * for the same staff-only/family-safe split applied to an earlier surface.
 */
function lastInitial(displayName: string | null | undefined): string {
  const parts = (displayName ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return '';
  const last = parts[parts.length - 1];
  return last ? `${last.charAt(0).toUpperCase()}.` : '';
}

/**
 * Two DIFFERENT humans who share a first name stay two entries - never
 * merged by label, only oneEntryPerHuman's identity link may ever collapse
 * two entries into one. Without this a Director sees "Ms. Ella" twice with
 * no way to tell whether that is one Jr Staff counted twice or two Jr Staff
 * who happen to share a name. Only a name that collides within THIS list
 * grows a last initial; a name that is already unique is left exactly as the
 * caller's own nameFor returned it.
 */
/** A trailing role note a staff label carries, e.g. " (Jr Staff)". */
const TRAILING_ROLE_NOTE = /\s*\([^()]*\)\s*$/;

/**
 * TWO DIFFERENT HUMANS WHO SHARE A LABEL, told apart by a last initial.
 * Staff surfaces only, for the reason lastInitial above spells out.
 *
 * Exported so the class builder's Jr assistant CHIPS can use the same rule
 * (review finding F4). They render teacherLabel(u) straight off the roster,
 * which is already one entry per human, so nothing there was a repeat - but
 * two DIFFERENT Jr Staff who share a first name drew two identical chips, and
 * a Director picking a teaching team could not tell which one she had just
 * tapped. Also run, second, on the lead teacher and additional teachers
 * pickers (review finding F2): disambiguateUnlinkedPickerClashes only
 * relabels a pair door two flagged as possibly the same person, so a real
 * same-name coincidence in those two pools needs this rule too, exactly as
 * the Jr assistants picker already gets it.
 */
export function disambiguateSameFirstName(
  entries: { name: string; user: UserDoc | null }[]
): string[] {
  const counts = new Map<string, number>();
  // A CLASH THE DIRECTORY CANNOT SEE ALL OF GETS NO INITIAL AT ALL (J11,
  // refute round 1). An entry with no account behind it is a seat this
  // viewer's own directory does not hold: a Manager's or an Admin's listener
  // reads active accounts only, so a Jr Staff's unpromoted dancer sign-in is
  // never in it, and oneEntryPerHuman cannot tell whether that seat is her or
  // somebody else. An initial spent on the clashing entries that DO have an
  // account ("Ms. Ella F. + Ms. Ella") then tells the reader they are two
  // different people, which nobody proved. Left bare, the names say only
  // what this screen knows. Two accounts the directory holds and does not
  // link are proven two people, so they still get their initials.
  const unseen = new Set<string>();
  for (const entry of entries) {
    if (!entry.name) continue;
    counts.set(entry.name, (counts.get(entry.name) ?? 0) + 1);
    if (!entry.user) unseen.add(entry.name);
  }
  return entries.map((entry) => {
    if (!entry.name || (counts.get(entry.name) ?? 0) < 2 || unseen.has(entry.name)) return entry.name;
    const initial = lastInitial(entry.user?.displayName);
    if (!initial) return entry.name;
    // THE INITIAL BELONGS TO THE NAME, so it lands BEFORE the role note:
    // "Ms. Ella R. (Jr Staff)", never "Ms. Ella (Jr Staff) R." (review
    // finding F5). teacherLabel in Offerings.tsx appends "(Jr Staff)" to the
    // name a Director reads, and an initial tacked on after the closing
    // bracket read as part of the role rather than part of the person.
    const note = TRAILING_ROLE_NOTE.exec(entry.name);
    if (!note) return `${entry.name} ${initial}`;
    const base = entry.name.slice(0, note.index).trimEnd();
    return base ? `${base} ${initial} ${note[0].trim()}` : `${entry.name} ${initial}`;
  });
}

/**
 * ONE HUMAN, ONE ENTRY, rendered: oneEntryPerHuman's collapse plus the
 * same-first-name disambiguator, in the one place both belong together so
 * every picker and card that renders a raw uid list gets both fixes from a
 * single call instead of re-implementing either. `nameFor` stays the
 * caller's own precedence (a stored name snapshot for an off-roster uid, a
 * family-safe name for a linked one) - this only decides WHICH uids survive
 * and whether two survivors need telling apart, never what a name says.
 *
 * STAFF SURFACES ONLY - see lastInitial's comment above. `nameFor` must
 * never be a family-facing name function, or the disambiguator could reach a
 * family screen through it.
 */
export function dedupedStaffNames(
  uids: readonly string[],
  users: readonly UserDoc[],
  nameFor: (uid: string, user: UserDoc | null) => string
): string[] {
  const byUid = new Map(users.map((u) => [u.uid, u]));
  const entries = oneEntryPerHuman(uids, users).map((uid) => {
    const user = byUid.get(uid) ?? null;
    return { name: nameFor(uid, user), user };
  });
  return disambiguateSameFirstName(entries);
}

/** One picker option this file needs to tell apart from another. `user` is
 * the account behind the option (null for a stale removal-only handle, which
 * a caller should never hand this file at all - see the doc comment below). */
export interface UnlinkedClashAccount {
  uid: string;
  label: string;
  user: UserDoc | null;
}

/** A possible-same-person pair, the exact shape possibleJrStaffDuplicates
 * (and duplicateJrStaffAdultAccounts) already return. */
export interface UnlinkedClashPair {
  ensembleUid: string;
  dancerLoginUid: string;
}

export interface UnlinkedClashResult {
  /** Index-aligned with the entries passed in. Untouched unless the entry's
   * label clashed with an entry from a pair door two flagged as possibly the
   * same person (review finding F2 - a genuine same-name coincidence with no
   * flag is left untouched here for disambiguateSameFirstName to handle). */
  labels: string[];
  /** At least one clash in this list is a flagged possible-same-person pair
   * (door two, possibleDuplicateUids) - a caller uses this to decide whether
   * to show the "link them in Manage Users" hint. Never true for two
   * genuinely different humans who happen to share a rendered name; there is
   * nothing to link for them. */
  hasPossibleDuplicateClash: boolean;
}

/**
 * isDancerLoginShapedUser above, not a raw `!!user.studentId` check: once an
 * identity is CONFIRMED, the server writes the identical studentId onto
 * BOTH accounts (the identity link IS users/{uid}.studentId), so studentId
 * presence alone stops meaning "this document is her own sign-in" the
 * moment jrStaffChoices (resolveJrStaffUsers, already one winner per
 * confirmed human) is the caller's source list - the winner there can be
 * either shape and both carry the designated dancer's studentId either way.
 * isDancerLoginShapedUser's jrStaffPromoted marker is what actually tells a
 * dancer-shaped document from a staff-shaped one regardless of who is
 * calling. isPromotedDancerLogin / isDancerLoginShapedUser mirror
 * isDancerLoginUserDoc in ./users, per this file's own note at the top.
 */
const isUnlinkedClashDancerSignIn = (entry: UnlinkedClashAccount): boolean =>
  !!entry.user && isDancerLoginShapedUser(entry.user);

/** Splices ", note" INSIDE a label's trailing "(...)" note rather than after
 * it - "Coach Carson (Jr Staff, dancer sign-in)", never "Coach Carson (Jr
 * Staff) (dancer sign-in)". Falls back to a fresh trailing note for a label
 * that does not already carry one (an Executive Teacher's plain name). */
function appendKindNote(label: string, note: string): string {
  const match = TRAILING_ROLE_NOTE.exec(label);
  if (!match) return `${label} (${note})`;
  const base = label.slice(0, match.index).trimEnd();
  const inner = match[0].trim().slice(1, -1).trim();
  return inner ? `${base} (${inner}, ${note})` : `${base} (${note})`;
}

/**
 * TWO ACCOUNTS, ONE LABEL, NOT ONE HUMAN: told apart, ONLY WHEN DOOR TWO
 * FLAGGED THAT EXACT PAIR AS POSSIBLY THE SAME PERSON.
 *
 * Bobbie's screenshot, 2026-09-02: the class builder's Additional teachers
 * picker printed "Coach Carson (Jr Staff)" twice - her own dancer sign-in and
 * her staff email account, not yet identity-linked. #917 already collapses a
 * LINKED pair to one entry (oneEntryPerHuman) and tells two DIFFERENT humans
 * apart on the Jr assistant picker (disambiguateSameFirstName). Neither
 * covers this shape: two UNLINKED accounts, same rendered label, that might
 * be one human wearing two shapes.
 *
 * NEVER MERGES, HIDES OR REORDERS ANYONE - every uid the caller passed stays
 * in the list, in the same order, under its own uid. This only rewrites a
 * label that would otherwise be indistinguishable from another entry's.
 *
 * WHAT COUNTS AS "ONE LINKED HUMAN": `aliasUids` (jrStaffUidAliases) maps
 * every uid of an identity-linked human to the same primary uid; two entries
 * that resolve to the same primary are one confirmed human and their shared
 * label is left BYTE IDENTICAL, exactly as it renders today.
 *
 * WHAT COUNTS AS "MIGHT BE ONE HUMAN" (review finding F2, fixing a bug an
 * adversarial review caught): only a pair `possibleDuplicatePairs`
 * (possibleJrStaffDuplicates, door two) actually flagged as possibly the same
 * person. The earlier version of this function relabeled ANY same-label pair
 * with different primaries, which caught two real people who simply share a
 * name too - two Ellas, two Mias, Carson Reed and Carson Blake - and told
 * them apart with "(dancer sign-in)" / "(staff account)", a note that asserts
 * a relationship neither of them has. Worse, when both were the same kind it
 * fell back to the email's local part or a dancer login handle to tell them
 * apart, which is a login credential printed as if it were a name - exactly
 * what no-email-as-name.test.mjs rule 1 forbids ("nobody's address is ever a
 * display name," and the same law covers a login handle). A flagged pair is
 * told apart by account kind:
 *
 *   - one is a dancer sign-in and the other a staff account: append
 *     ", dancer sign-in" / ", staff account" to each (the reported shape).
 *   - the rare case both flagged entries read as the same kind: the first
 *     stays plain and the next counts from 2 ("staff account 2", ...) rather
 *     than inventing a fact from nothing. Door two only ever flags one
 *     ensemble uid against one dancer-login uid, so this branch is a defensive
 *     fallback, not a shape that happens today.
 *
 * A pair that is NOT flagged is left exactly as the caller built it - two
 * different humans who share a label is disambiguateSameFirstName's rule to
 * apply (the #917 last-initial rule), not this function's, and
 * `hasPossibleDuplicateClash` only ever reflects a REAL flagged relation, so
 * the "link them in Manage Users" hint is never shown for two people there is
 * nothing to link.
 */
export function disambiguateUnlinkedPickerClashes(
  entries: readonly UnlinkedClashAccount[],
  aliasUids: ReadonlyMap<string, string>,
  possibleDuplicatePairs: readonly UnlinkedClashPair[]
): UnlinkedClashResult {
  const labels = entries.map((e) => e.label);
  const primaryOf = (uid: string) => aliasUids.get(uid) ?? uid;

  const flaggedPairs = new Set<string>();
  for (const pair of possibleDuplicatePairs) {
    flaggedPairs.add(`${pair.ensembleUid}|${pair.dancerLoginUid}`);
    flaggedPairs.add(`${pair.dancerLoginUid}|${pair.ensembleUid}`);
  }
  const isFlaggedWithAnother = (indices: readonly number[], i: number): boolean =>
    indices.some((j) => j !== i && flaggedPairs.has(`${entries[i].uid}|${entries[j].uid}`));

  const byLabel = new Map<string, number[]>();
  entries.forEach((entry, index) => {
    const at = byLabel.get(entry.label);
    if (at) at.push(index);
    else byLabel.set(entry.label, [index]);
  });

  let hasPossibleDuplicateClash = false;

  for (const indices of byLabel.values()) {
    if (indices.length < 2) continue;
    // Every index here shares one label. If they also share one primary uid
    // they are one CONFIRMED human (#917) - nothing to tell apart, and the
    // label stays exactly as the caller built it.
    const primaries = new Set(indices.map((i) => primaryOf(entries[i].uid)));
    if (primaries.size < 2) continue;

    // ONLY the indices door two actually flagged against another index in
    // THIS group get a kind note. Everyone else in the group - a genuine
    // same-name coincidence - is left byte identical for
    // disambiguateSameFirstName to sort out.
    const flaggedIdx = indices.filter((i) => isFlaggedWithAnother(indices, i));
    if (flaggedIdx.length < 2) continue;

    const dancerIdx = flaggedIdx.filter((i) => isUnlinkedClashDancerSignIn(entries[i]));
    const staffIdx = flaggedIdx.filter((i) => !isUnlinkedClashDancerSignIn(entries[i]));

    // Never an email local part or a login handle (review finding F2): the
    // first stays plain, later ones in the same kind count from 2, an honest
    // limit rather than an invented fact.
    const notesFor = (idxGroup: number[], kind: string): string[] =>
      idxGroup.map((_, pos) => (pos === 0 ? kind : `${kind} ${pos + 1}`));

    const dancerNotes = notesFor(dancerIdx, 'dancer sign-in');
    dancerIdx.forEach((i, pos) => {
      labels[i] = appendKindNote(entries[i].label, dancerNotes[pos]);
    });
    const staffNotes = notesFor(staffIdx, 'staff account');
    staffIdx.forEach((i, pos) => {
      labels[i] = appendKindNote(entries[i].label, staffNotes[pos]);
    });

    hasPossibleDuplicateClash = true;
  }

  return { labels, hasPossibleDuplicateClash };
}

/** What placeStoredUidsOnPool worked out for one picker. */
export interface StoredUidPlacement {
  /** Index aligned with the pool passed in: the uid each pool entry renders
   * under. A pool entry whose human is already on this class under a
   * DIFFERENT uid of hers renders under that stored uid instead of her own,
   * so the stored value still matches an option. Never reordered, never
   * added to, never dropped. */
  renderUids: string[];
  /** A stored uid that belongs to a pool human who is ALREADY rendering
   * under another stored uid of hers - the shape where one class holds both
   * of one person's uids. `poolIndex` names which pool entry she is, so a
   * caller can label the leftover with her real name instead of calling a
   * live account "no longer a teacher". */
  extras: { uid: string; poolIndex: number }[];
}

/**
 * A CLASS ALREADY ASSIGNED UNDER HER OTHER UID IS STILL HER CLASS.
 *
 * oneEntryPerHuman collapses two identity-linked accounts to one picker
 * entry, which is right for the list and dangerous for a class that was
 * saved before the pair was confirmed: that class holds the uid that just
 * lost, so the picker no longer offers the value the form is holding. The
 * lead teacher <select> then falls through to the removal-only branch and
 * tells a Director her real teacher is "No longer a teacher account", and
 * the next save writes that back. jrStaffUidAliases' own note names this
 * exact failure ("a picker that only recognises the SURVIVING uid quietly
 * breaks the promise anyway"), and the Jr assistants picker already avoids
 * it by resolving a stored uid through primaryUidFor.
 *
 * So this hands the caller the same recognition for the lead and additional
 * teacher pools: one entry per human, rendered under the uid this class
 * already holds. The SAVE PATH IS UNTOUCHED - the option carries the stored
 * uid, so a form that never touches the field writes back exactly the uid it
 * read, and no assignment is silently moved from one account to another.
 *
 * Only IDENTITY-LINKED uids are ever swapped (aliasUids, written from
 * users/{uid}.studentId by the server). A possible-duplicate flag never
 * reaches here, for the reason jrStaffUidAliases spells out: a sibling's
 * account wears that shape identically.
 */
export function placeStoredUidsOnPool(
  poolUids: readonly string[],
  storedUids: readonly string[],
  aliasUids: ReadonlyMap<string, string>
): StoredUidPlacement {
  const primaryOf = (uid: string) => aliasUids.get(uid) ?? uid;
  const renderUids = [...poolUids];
  const extras: { uid: string; poolIndex: number }[] = [];
  const poolSet = new Set(poolUids);
  const storedSet = new Set(storedUids);
  const byPrimary = new Map<string, number[]>();
  poolUids.forEach((uid, index) => {
    const primary = primaryOf(uid);
    const at = byPrimary.get(primary);
    if (at) at.push(index);
    else byPrimary.set(primary, [index]);
  });
  const taken = new Set<number>();
  for (const stored of storedUids) {
    // Already offered under its own uid - nothing to place.
    if (poolSet.has(stored)) continue;
    const candidates = byPrimary.get(primaryOf(stored));
    // Not one of this pool's people at all. The caller's own stale branch is
    // the honest answer for that uid, and this must not invent an entry.
    if (!candidates || candidates.length === 0) continue;
    const slot = candidates.find(
      (index) => !taken.has(index) && !storedSet.has(poolUids[index] ?? '')
    );
    if (slot === undefined) {
      extras.push({ uid: stored, poolIndex: candidates[0] as number });
      continue;
    }
    taken.add(slot);
    renderUids[slot] = stored;
  }
  return { renderUids, extras };
}


/**
 * QUESTION (a) FOR A WHOLE LIST AT ONCE. The dancers who are Jr Staff, as a
 * set a row can test in constant time.
 *
 * It exists so a roster page cannot be tempted back into
 * `dancerHasLinkedRole(id, users, 'ensemble')` per row for speed: that call
 * is what left a designated Jr Staff with only a dancer sign-in showing no
 * Jr Staff pill anywhere in the app, because it asks about an ACCOUNT and
 * the designation is the fact that makes somebody Jr Staff.
 */
export function jrStaffStudentIds(source: JrStaffSource): Set<string> {
  return new Set(
    resolveJrStaffIdentities(source)
      .map((identity) => identity.studentId)
      .filter((id): id is string => typeof id === 'string' && id.length > 0)
  );
}
